import { useEffect, useRef, RefObject, useState, useMemo } from 'react';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { Copy, Check, CheckCircle, ThumbsDown, ThumbsUp, RefreshCw } from 'lucide-react';
import { RiLightbulbLine } from '@remixicon/react';

import { PopoverPortal } from '@/components/molecules/Popover';
import { usePopoverController } from '@/components/molecules/Popover/usePopoverController';
import { StreamingText } from '@/components/molecules/StreamingText';

import { Tooltip } from '@/components/molecules/Tooltip';

import { Widget, insightsFeedbackEndpoints } from '@/modules';
import { useWidgetRecommendationsWebSocket } from '@/modules/websocket';
import { useUser } from '@/modules/auth/context/UserContext';
import { useDashboard } from '@/modules/dashboard/context/DashboardContext';
import { Button, Textarea } from '@/components/atoms';
import { copyInsightsToClipboard } from '@/utils/clipboardUtils';
import { applyGlossaryTerms } from '@/utils/glossaryTerms';
import { useGlossaryTerms } from '@/hooks/useGlossaryTerms';

// Same rendering approach as WidgetInsights' InsightBullets (duplicated there too) -
// parses the generated HTML into <li>/<p> items and streams each one in.
const RecommendationBullets: React.FC<{ html: string; className?: string }> = ({ html, className }) => {
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
    const [animatedCount, setAnimatedCount] = useState(0);

    const parsed = useMemo(() => {
        const div = document.createElement('div');
        div.innerHTML = html;
        const lis = Array.from(div.querySelectorAll('li'));
        if (lis.length > 0) {
            return { isList: true, items: lis.map(li => ({ html: li.innerHTML, text: li.textContent?.replace(/\s+/g, ' ').trim() || '' })) };
        }
        const ps = Array.from(div.querySelectorAll('p'));
        if (ps.length > 0) {
            return { isList: false, items: ps.map(p => ({ html: p.innerHTML, text: p.textContent?.replace(/\s+/g, ' ').trim() || '' })) };
        }
        return null;
    }, [html]);

    const copyItem = async (text: string, index: number) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch {}
    };

    if (!parsed) {
        return <StreamingText text={html} speed={25} startDelay={300} className={className} />;
    }

    const renderItem = (item: { html: string; text: string }, i: number) => {
        const isDone = i < animatedCount;
        const isAnimating = i === animatedCount;
        if (!isDone && !isAnimating) return null;
        return (
            <div className="group flex items-start gap-1.5">
                <span className="flex-1">
                    {isAnimating ? (
                        <StreamingText
                            text={item.html}
                            speed={25}
                            startDelay={i === 0 ? 300 : 0}
                            onComplete={() => setAnimatedCount(c => c + 1)}
                        />
                    ) : (
                        <span dangerouslySetInnerHTML={{ __html: item.html }} />
                    )}
                </span>
                {isDone && (
                    <button
                        onClick={() => copyItem(item.text, i)}
                        className="flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-100 cursor-pointer"
                        title="Copy"
                    >
                        {copiedIndex === i
                            ? <Check className="w-3 h-3 text-green-600" />
                            : <Copy className="w-3 h-3 text-gray-400" />
                        }
                    </button>
                )}
            </div>
        );
    };

    if (parsed.isList) {
        return (
            <ul className={className} style={{ paddingLeft: '1.25rem', listStyleType: 'disc' }}>
                {parsed.items.map((item, i) => {
                    const content = renderItem(item, i);
                    if (!content) return null;
                    return <li key={i} style={{ display: 'list-item' }}>{content}</li>;
                })}
            </ul>
        );
    }

    return (
        <div className={classNames(className, 'space-y-2')}>
            {parsed.items.map((item, i) => {
                const content = renderItem(item, i);
                if (!content) return null;
                return <div key={i}>{content}</div>;
            })}
        </div>
    );
};

interface IWidgetRecommendationsProps {
    widget: Widget;
    widgetData?: any;
    externalController?: ReturnType<typeof usePopoverController>;
    hideTrigger?: boolean;
    children?: React.ReactNode;
    anchorRef?: RefObject<HTMLElement | null>;
}

export const WidgetRecommendations: React.FC<IWidgetRecommendationsProps> = ({ widget, widgetData, externalController, hideTrigger = false, children, anchorRef }) => {
    const internalController = usePopoverController();
    const controller = externalController || internalController;
    const { recommendationsState, sendRequest, reset } = useWidgetRecommendationsWebSocket();
    const glossaryTerms = useGlossaryTerms();
    const defaultAnchorRef = useRef<HTMLDivElement>(null);
    const triggerRef = anchorRef || defaultAnchorRef;
    const [copied, setCopied] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const { user } = useUser();
    const { state: { selectedDateRange, selectedCompareEnabled, selectedCompareDateRange, selectedDealer, dealerContractedProducts } } = useDashboard();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState<"positive" | "negative" | null>(null);
    const [form, setForm] = useState<{ sentiment: "positive" | "negative" | null, feedback: string }>({ sentiment: null, feedback: '' });

    const lastGeneratedContextRef = useRef<string>('');
    const contextKey = [
        widget.id,
        selectedDealer?.dealerRefId ?? '',
        selectedDateRange?.from?.toISOString() ?? '',
        selectedDateRange?.to?.toISOString() ?? '',
    ].join('|');

    const recommendations = recommendationsState.status === "done" ? recommendationsState.recommendations : null;

    const buildRequest = () => ({
        ...widget,
        data: widgetData,
        ...(widget.datePeriod === 'dashboard_date' && selectedDateRange && {
            filters: {
                ...((widget as any).filters ?? {}),
                dateRange: {
                    from: dayjs(selectedDateRange.from).format('YYYY-MM-DD'),
                    to: dayjs(selectedDateRange.to).format('YYYY-MM-DD'),
                }
            }
        })
    });

    const sendFeedback = async () => {
        if (!user?.userId || !recommendations || !form.sentiment || submitted) return;

        try {
            setSubmitting(true);
            await insightsFeedbackEndpoints.createInsightsFeedback({
                userId: user?.userId ?? '',
                type: "widget-recommendation",
                sentiment: form.sentiment,
                insight: recommendations,
                feedback: form.feedback,
                data: {
                    ...widget,
                    data: widgetData,
                    dashboardDateRange: selectedDateRange
                        ? { from: selectedDateRange.from.toISOString(), to: selectedDateRange.to.toISOString() }
                        : null,
                    dashboardCompareEnabled: Boolean(selectedCompareEnabled),
                    dashboardCompareDateRange: selectedCompareDateRange
                        ? { from: selectedCompareDateRange.from.toISOString(), to: selectedCompareDateRange.to.toISOString() }
                        : null,
                },
            });
            setSubmitted(form.sentiment);
            setForm({ sentiment: null, feedback: '' });
        } catch (error) {
            console.error("Error sending recommendations feedback:", error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = async () => {
        if (!recommendations) return;
        const success = await copyInsightsToClipboard(recommendations);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleRefresh = () => {
        setSubmitted(null);
        setForm({ sentiment: null, feedback: '' });
        reset();
        lastGeneratedContextRef.current = contextKey;
        sendRequest(buildRequest(), selectedDealer?.dealerRefId ?? '', selectedDealer?.oemProgramName, dealerContractedProducts?.mappedProductsBudget);
    };

    useEffect(() => {
        if (controller.isOpen) {
            setIsMinimized(false);
            setSubmitted(null);
            setForm({ sentiment: null, feedback: '' });
            const contextChanged = lastGeneratedContextRef.current !== contextKey;
            if (recommendationsState.status !== "done" || contextChanged) {
                reset();
                lastGeneratedContextRef.current = contextKey;
                sendRequest(buildRequest(), selectedDealer?.dealerRefId ?? '', selectedDealer?.oemProgramName, dealerContractedProducts?.mappedProductsBudget);
            }
        }
    }, [controller.isOpen, contextKey]);


    const trigger = hideTrigger ? (
        <div ref={triggerRef as RefObject<HTMLDivElement>} className="absolute right-0 top-0 w-8 h-8 pointer-events-none opacity-0" />
    ) : (
        <Tooltip content="Recommendations">
            <div onClick={controller.open} className="transition-all duration-200 cursor-pointer ease-in-out" style={{ color: '#1F8894' }}>
                <RiLightbulbLine className="w-5 h-5" />
            </div>
        </Tooltip>
    );

    return (
        <PopoverPortal
            draggable
            align='right'
            zIndex="z-[55]"
            className={classNames('ring-2', 'ring-[#1F8894]')}
            trigger={trigger as React.ReactElement}
            width="w-96"
            controller={controller}
            isMinimized={isMinimized}
            onMinimize={() => setIsMinimized(true)}
            onMaximize={() => setIsMinimized(false)}
            onClickOutside={() => setIsMinimized(true)}
            minimizedLabel={
                <span className="flex items-center gap-1.5" style={{ color: '#1F8894' }}>
                    <RiLightbulbLine className="w-3.5 h-3.5" />
                    <span className="text-xs font-semibold font-roboto">Recommendations</span>
                </span>
            }
        >
            <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2" style={{ color: '#1F8894' }}>
                        <RiLightbulbLine className="w-5 h-5" />
                        <h3 className="text-lg font-semibold font-roboto">Recommendations</h3>
                    </div>
                    <div className="flex items-center gap-1">
                        {(recommendationsState.status === "done" || recommendationsState.status === "error") && (
                            <Tooltip content="Refresh">
                                <button
                                    onClick={handleRefresh}
                                    className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                                    aria-label="Refresh"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                </button>
                            </Tooltip>
                        )}
                        {recommendations && (
                            <Tooltip content={copied ? "Copied!" : "Copy to clipboard"}>
                                <button
                                    onClick={handleCopy}
                                    className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                                    aria-label="Copy to clipboard"
                                >
                                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                </button>
                            </Tooltip>
                        )}
                    </div>
                </div>

                {recommendationsState.status === "loading" && (
                    <div className="text-sm text-gray-500 font-roboto">
                        <StreamingText key={recommendationsState.step} text={recommendationsState.step} speed={25} startDelay={0} />
                    </div>
                )}

                {recommendationsState.status === "error" && (
                    <div className="text-sm text-red-500 font-roboto">{recommendationsState.error}</div>
                )}

                {recommendationsState.status === "done" && !recommendations && (
                    <div className="text-sm text-gray-500 font-roboto">No recommendations available</div>
                )}

                {recommendationsState.status === "done" && recommendations && (
                    <>
                        <RecommendationBullets
                            html={applyGlossaryTerms(recommendations, glossaryTerms)}
                            className="text-sm text-gray-700 leading-relaxed font-roboto"
                        />

                        {!form.sentiment && !submitted && (
                            <div className="mt-3 flex items-center justify-end gap-1">
                                <Tooltip content="Positive">
                                    <button
                                        onClick={() => setForm(prev => ({ ...prev, sentiment: "positive" }))}
                                        disabled={submitting || !user?.userId}
                                        className={classNames("p-1 rounded cursor-pointer hover:bg-gray-100 transition-colors disabled:opacity-50", {
                                            'text-green-500': submitted === "positive",
                                            'text-gray-500 hover:text-gray-700': submitted !== "positive",
                                        })}
                                        aria-label="Positive"
                                    >
                                        <ThumbsUp className="w-4 h-4" />
                                    </button>
                                </Tooltip>
                                <Tooltip content="Negative">
                                    <button
                                        onClick={() => setForm(prev => ({ ...prev, sentiment: "negative" }))}
                                        disabled={submitting || !user?.userId}
                                        className={classNames("p-1 rounded cursor-pointer hover:bg-gray-100 transition-colors disabled:opacity-50", {
                                            'text-red-500': submitted === "negative",
                                            'text-gray-500 hover:text-gray-700': submitted !== "negative",
                                        })}
                                        aria-label="Negative"
                                    >
                                        <ThumbsDown className="w-4 h-4" />
                                    </button>
                                </Tooltip>
                            </div>
                        )}

                        {form.sentiment && !submitted && (
                            <div className="mt-3">
                                <Textarea onKeyDown={e => e.stopPropagation()} onChange={(e) => setForm(prev => ({ ...prev, feedback: e.target.value }))} placeholder='Write your feedback' />
                                <div className={classNames('w-full', 'mt-3', 'flex', 'justify-end', 'gap-2')}>
                                    <Button disabled={submitting} variant="light" size="xs" onClick={() => setForm(prev => ({ ...prev, sentiment: null }))}>Cancel</Button>
                                    <Button isLoading={submitting} disabled={submitting || (form.sentiment === "negative" && !form.feedback.trim())} variant="primary" size="xs" onClick={() => sendFeedback()}>Submit</Button>
                                </div>
                            </div>
                        )}

                        {submitted && (
                            <div className="mt-3 text-sm bg-emerald-50 p-2 rounded flex justify-center items-center gap-3">
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                                <p className={classNames('font-medium', 'text-slate-800')}>Feedback sent</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </PopoverPortal>
    );
};
